/** @jsxImportSource @emotion/react */
import { useEffect } from "react";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useGetDeliveryGroup } from "../../../service/api";
import {
  DeliveryRuleForm,
  Rule,
} from "../../../service/api-response/DeliveryRule";
import { DeliveryRulesModalBody } from "./DeliveryRulesModalBody";
import { SpinnerBlock } from "../../common/Spinner";
import { Modal } from "../../../components/Modal";
import { getCountrySpecificValues, transformPriceFromAPI } from "../../../util";
import { validateAboveZero } from "../../../components/forms/inputs/PriceInput";
import { useHandleFetchError } from "../../../error-handler";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  ruleId?: string;
};

const ruleSchema = yup.object().shape({
  minimumDistance: yup
    .number()
    .typeError("Distance must be a number")
    .min(0)
    .required("Choose minimum distance for this rule"),
  maximumDistance: yup
    .number()
    .typeError("Distance must be a number")
    .positive("Distance must be positive")
    .required("Choose maximum distance for this rule"),
  productId: yup.string().required("Choose delivery product for this distance"),
});

const deliveryRuleSchema = yup.object().shape({
  name: yup.string().when("$isEdit", (isEdit, schema) => {
    if (!isEdit) {
      return schema.required("Enter delivery rule group name to continue");
    }
  }),
  bufferProductId: yup.string().when("$isEdit", (isEdit, schema) => {
    if (!isEdit) {
      return schema.required("Choose delivery extra fee product");
    }
  }),
  minimumPriceAmount: validateAboveZero("minimumPriceCurrency").required(
    "Choose minimum price to purchase for"
  ),
  minimumPriceCurrency: yup
    .string()
    .required("Choose currency for minimum price"),
  rules: yup.array().of(ruleSchema),
});

export const DeliveryRulesModal = ({ isOpen, onClose, ruleId }: Props) => {
  const organization = useSelector(authSelectors.selectedOrganizationKeyName);
  const isEdit = !!ruleId;
  const { currency } = getCountrySpecificValues();
  const methods = useForm<DeliveryRuleForm>({
    /* @ts-ignore */
    resolver: yupResolver(deliveryRuleSchema),
    defaultValues: {
      minimumPriceCurrency: currency,
    },
    context: {
      isEdit: isEdit,
    },
  });

  const { reset } = methods;

  const {
    data: deliveryRulesGroup,
    isLoading: deliveryRulesGroupIsLoading,
    error: getDeliveryRuleGroupError,
  } = useGetDeliveryGroup(organization, ruleId, isOpen);

  useHandleFetchError(getDeliveryRuleGroupError);

  useEffect(() => {
    if (!deliveryRulesGroup) {
      return;
    }

    const sortedRules = deliveryRulesGroup.rules.sort(
      (a: Rule, b: Rule) => a.minimumDistance - b.minimumDistance
    );

    reset({
      minimumPriceAmount: transformPriceFromAPI(
        deliveryRulesGroup.minimumDeliveryPrice.value,
        deliveryRulesGroup.minimumDeliveryPrice.currency
      ),
      minimumPriceCurrency: deliveryRulesGroup.minimumDeliveryPrice.currency,
      rules: sortedRules,
    });
  }, [deliveryRulesGroup, reset]);

  return (
    /* @ts-ignore */
    <FormProvider {...methods}>
      <Modal
        header={`${isEdit ? "Edit" : "Create"} delivery rule group`}
        isOpen={isOpen}
        onClose={onClose}
        width="850px"
      >
        {deliveryRulesGroupIsLoading ? (
          <SpinnerBlock />
        ) : (
          <form>
            <DeliveryRulesModalBody
              isEdit={isEdit}
              onClose={onClose}
              ruleId={ruleId}
            />
          </form>
        )}
      </Modal>
    </FormProvider>
  );
};
